import { useState, useEffect, useRef, useCallback } from 'react'

export interface SseEvent {
  id: string
  type: string
  employeeNo?: string
  userName?: string
  membershipStatus?: 'active' | 'expiring_today' | 'none'
  doorNo?: number
  timestamp: string
  raw?: unknown
}

export interface SseState {
  events: SseEvent[]
  connected: boolean
}

export function useSSE(proxyUrl: string | null, proxyToken: string | null, deviceIds: string[]): SseState {
  const [events, setEvents] = useState<SseEvent[]>([])
  const [connected, setConnected] = useState(false)
  const esRef = useRef<EventSource | null>(null)
  const retryRef = useRef(1000)
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const connect = useCallback(() => {
    if (!proxyUrl || !proxyToken || deviceIds.length === 0) return

    const url = `${proxyUrl}/stream?devices=${deviceIds.join(',')}&proxyToken=${proxyToken}`
    const es = new EventSource(url)
    esRef.current = es

    es.onopen = () => {
      setConnected(true)
      retryRef.current = 1000
    }

    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data) as SseEvent
        setEvents((prev) => [data, ...prev].slice(0, 50))
      } catch { /* ignore malformed */ }
    }

    es.onerror = () => {
      setConnected(false)
      es.close()
      esRef.current = null
      const delay = Math.min(retryRef.current, 30000)
      retryRef.current = Math.min(retryRef.current * 2, 30000)
      retryTimer.current = setTimeout(connect, delay)
    }
  }, [proxyUrl, deviceIds.join(',')]) // eslint-disable-line

  useEffect(() => {
    if (!proxyUrl || !proxyToken) return
    connect()
    return () => {
      esRef.current?.close()
      if (retryTimer.current) clearTimeout(retryTimer.current)
    }
  }, [connect, proxyUrl, proxyToken])

  return { events, connected }
}
